import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { isAccountType } from "@common/constants/domains/accounting/helpers";
import {
    AccountRolePresence,
    AccountRulePresence,
    AccountSource,
    AccountStatus,
} from "@common/types/domains/accounting/account";

export interface GroupNameChainNode {
    id: string;
    name: string;
    parentGroupId: string | null;
}

export const resolveGroupChain = (
    parentGroupId: string | null,
    groupsById: ReadonlyMap<string, GroupNameChainNode>,
): GroupNameChainNode[] => {
    const chain: GroupNameChainNode[] = [];
    const visited = new Set<string>();
    let currentId = parentGroupId;
    while (currentId) {
        // Truncate cyclic/dangling chains so malformed data cannot loop.
        if (visited.has(currentId)) {
            break;
        }
        visited.add(currentId);
        const group = groupsById.get(currentId);
        if (!group) {
            break;
        }
        chain.unshift(group);
        currentId = group.parentGroupId;
    }
    return chain;
};

export const resolveGroupNameChain = (
    parentGroupId: string | null,
    groupsById: ReadonlyMap<string, GroupNameChainNode>,
): string[] =>
    resolveGroupChain(parentGroupId, groupsById).map((group) => group.name);

export interface AccountFilterFields {
    name: string;
    codeText: string | null;
    type: AccountType | null | undefined;
    entityId: number;
    isDisabled: boolean;
    isManuallyCreated: boolean;
    ancestorGroupNames: string[];
    hasRole: boolean;
    hasRule?: boolean;
    searchLabels?: readonly string[];
}

export interface AccountFilterCriteria {
    search?: string;
    accountStatus?: AccountStatus;
    accountSource?: AccountSource;
    accountRolePresence?: AccountRolePresence;
    accountRulePresence?: AccountRulePresence;
    // undefined keeps every business; [] is an empty tree.
    displayedEntityIds?: number[];
}

const matchesSearch = (
    account: AccountFilterFields,
    query: string,
): boolean => {
    const haystacks = [account.name, account.codeText ?? ""];
    if (account.type) {
        haystacks.push(account.type);
        if (isAccountType(account.type)) {
            // so "cogs" also matches "Cost of Goods Sold"
            haystacks.push(getPredefinedGroupLabel(account.type));
        }
    }
    haystacks.push(...account.ancestorGroupNames);
    haystacks.push(...(account.searchLabels ?? []));
    return haystacks.some((value) => value.toLowerCase().includes(query));
};

export const accountMatchesFilters = (
    account: AccountFilterFields,
    criteria: AccountFilterCriteria,
): boolean => {
    if (criteria.accountStatus) {
        const shouldBeDisabled =
            criteria.accountStatus === AccountStatus.DISABLED;
        if (account.isDisabled !== shouldBeDisabled) {
            return false;
        }
    }
    if (criteria.accountSource) {
        const shouldBeManual = criteria.accountSource === AccountSource.CUSTOM;
        if (account.isManuallyCreated !== shouldBeManual) {
            return false;
        }
    }
    if (criteria.accountRolePresence) {
        const shouldHaveRole =
            criteria.accountRolePresence === AccountRolePresence.HAS_ROLE;
        if (account.hasRole !== shouldHaveRole) {
            return false;
        }
    }
    if (criteria.accountRulePresence) {
        if (account.hasRule === undefined) {
            return false;
        }
        const shouldHaveRule =
            criteria.accountRulePresence === AccountRulePresence.HAS_RULE;
        if (account.hasRule !== shouldHaveRule) {
            return false;
        }
    }
    if (
        criteria.displayedEntityIds &&
        !criteria.displayedEntityIds.includes(account.entityId)
    ) {
        return false;
    }
    const query = criteria.search?.trim().toLowerCase();
    if (query && !matchesSearch(account, query)) {
        return false;
    }
    return true;
};
